// InvoiceExportButton.js
import React, { useState } from 'react';
import { Button, Modal, Form, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { Download, FileSpreadsheet, Calendar } from 'lucide-react';
import InvoiceExportService from './InvoiceExportService';

const InvoiceExportButton = ({ sales, boutiqueInfo }) => {
  const [showModal, setShowModal] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [exporting, setExporting] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState('success');
  const [useAllDates, setUseAllDates] = useState(true);

  // Ouvrir le modal
  const handleShow = () => {
    setShowModal(true);
    setMessage(null);
    // Initialiser avec les dates du jour
    const today = new Date().toISOString().split('T')[0];
    setStartDate(today);
    setEndDate(today);
  };

  // Fermer le modal
  const handleClose = () => {
    setShowModal(false);
    setStartDate('');
    setEndDate('');
    setUseAllDates(true);
    setMessage(null);
  };

  // Gérer l'export
  const handleExport = async () => {
    try {
      setExporting(true);
      setMessage(null);

      // Validation des dates si nécessaire
      if (!useAllDates) {
        if (!startDate && !endDate) {
          setMessage('Veuillez sélectionner au moins une date');
          setMessageType('warning');
          setExporting(false);
          return;
        }

        if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
          setMessage('La date de début doit être antérieure à la date de fin');
          setMessageType('warning');
          setExporting(false);
          return;
        }
      }

      // Préparer les options d'export
      const options = {
        boutiqueInfo
      };

      if (!useAllDates) {
        if (startDate) options.startDate = new Date(startDate);
        if (endDate) {
          // Ajouter 23h59 à la date de fin pour inclure toute la journée
          const endDateTime = new Date(endDate);
          endDateTime.setHours(23, 59, 59, 999);
          options.endDate = endDateTime;
        }
      }

      // Exécuter l'export
      const result = InvoiceExportService.exportInvoicesToExcel(sales, options);

      if (result.success) {
        setMessage(result.message);
        setMessageType('success');
        
        // Afficher les statistiques
        if (result.stats) {
          const statsMessage = `
            ${result.message}
            Total: ${new Intl.NumberFormat('fr-FR').format(result.stats.totalVentes)} XOF
            Moyenne: ${new Intl.NumberFormat('fr-FR').format(result.stats.moyenneVente)} XOF
          `;
          setMessage(statsMessage);
        }

        // Fermer automatiquement après 3 secondes
        setTimeout(() => {
          handleClose();
        }, 3000);
      } else {
        setMessage(result.message);
        setMessageType('danger');
      }

    } catch (error) {
      console.error('❌ Erreur export:', error);
      setMessage('Une erreur est survenue lors de l\'export');
      setMessageType('danger');
    } finally {
      setExporting(false);
    }
  };

  // Raccourcis de dates
  const setQuickDate = (type) => {
    const today = new Date();
    const start = new Date(today);
    
    switch (type) {
      case 'today':
        setStartDate(today.toISOString().split('T')[0]);
        setEndDate(today.toISOString().split('T')[0]);
        setUseAllDates(false);
        break;
      case 'week':
        start.setDate(today.getDate() - 7);
        setStartDate(start.toISOString().split('T')[0]);
        setEndDate(today.toISOString().split('T')[0]);
        setUseAllDates(false);
        break;
      case 'month':
        start.setMonth(today.getMonth() - 1);
        setStartDate(start.toISOString().split('T')[0]);
        setEndDate(today.toISOString().split('T')[0]);
        setUseAllDates(false);
        break;
      case 'year':
        start.setFullYear(today.getFullYear() - 1);
        setStartDate(start.toISOString().split('T')[0]);
        setEndDate(today.toISOString().split('T')[0]);
        setUseAllDates(false);
        break;
      default:
        break;
    }
  };

  return (
    <>
      {/* Bouton d'export */}
      <Button
        variant="success"
        onClick={handleShow}
        className="d-flex align-items-center gap-2"
      >
        <FileSpreadsheet size={18} />
        Exporter en Excel
      </Button>

      {/* Modal de configuration d'export */}
      <Modal show={showModal} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            <Download size={24} className="text-success" />
            Exporter les factures
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {message && (
            <Alert variant={messageType} className="mb-3">
              {message.split('\n').map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </Alert>
          )}

          {/* Option: Toutes les factures */}
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              id="useAllDates"
              label="Exporter toutes les factures (sans filtre de date)"
              checked={useAllDates}
              onChange={(e) => setUseAllDates(e.target.checked)}
            />
          </Form.Group>

          {/* Sélection de dates */}
          {!useAllDates && (
            <>
              <div className="mb-3">
                <h6 className="text-muted mb-2">Raccourcis rapides</h6>
                <div className="d-flex flex-wrap gap-2">
                  <Button 
                    size="sm" 
                    variant="outline-primary"
                    onClick={() => setQuickDate('today')}
                  >
                    Aujourd'hui
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline-primary"
                    onClick={() => setQuickDate('week')}
                  >
                    7 derniers jours
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline-primary"
                    onClick={() => setQuickDate('month')}
                  >
                    30 derniers jours
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline-primary"
                    onClick={() => setQuickDate('year')}
                  >
                    1 an
                  </Button>
                </div>
              </div>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="d-flex align-items-center gap-2">
                      <Calendar size={16} />
                      Date de début
                    </Form.Label>
                    <Form.Control
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      max={endDate || new Date().toISOString().split('T')[0]}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="d-flex align-items-center gap-2">
                      <Calendar size={16} />
                      Date de fin
                    </Form.Label>
                    <Form.Control
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      min={startDate}
                      max={new Date().toISOString().split('T')[0]}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}

          {/* Informations sur l'export */}
          <Alert variant="info" className="mb-0">
            <small>
              <strong>Le fichier Excel contiendra :</strong>
              <ul className="mb-0 mt-2">
                <li>Résumé de toutes les factures</li>
                <li>Détails des articles vendus</li>
                <li>Statistiques de ventes</li>
              </ul>
            </small>
          </Alert>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Annuler
          </Button>
          <Button
            variant="success"
            onClick={handleExport}
            disabled={exporting}
            className="d-flex align-items-center gap-2"
          >
            {exporting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
                Export en cours...
              </>
            ) : (
              <>
                <Download size={18} />
                Exporter
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default InvoiceExportButton;